import { useState, type ReactNode } from 'react';
import { Cloud, Laptop, RefreshCw } from 'lucide-react';
import {
  appModeBlockedLabel,
  type BlockingPreview,
  type SetAppModeResult,
} from '@volley/crucible-ipc-types';
import { useIpcQuery } from '../hooks/useIpc';
import { useClientCapabilities } from '../transport/context';
import { Button } from './ui/button';

export function AppModeSwitchButtonChrome({
  label,
  icon,
  onClick,
  disabled,
  title,
}: {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}): React.JSX.Element {
  // Uses the shared bordered Button (secondary variant) so the mode-switch
  // controls match every other secondary button in the app.
  return (
    <Button
      variant="secondary"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={label}
    >
      {icon}
      <span>{label}</span>
    </Button>
  );
}

export function SwitchToLocalFoundryButtonChrome({
  onClick,
}: {
  onClick: () => void;
}): React.JSX.Element {
  return (
    <AppModeSwitchButtonChrome
      label="Switch to Local Foundry"
      icon={<Laptop size={14} />}
      onClick={onClick}
    />
  );
}

export function SwitchToCloudFoundryButton({
  className = '',
}: {
  className?: string;
}): React.JSX.Element | null {
  const capabilities = useClientCapabilities();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #2259 — when the switch is blocked by a local preview, hold the offending
  // preview(s) so we can list them and offer a one-click stop-and-switch.
  const [blocking, setBlocking] = useState<BlockingPreview[] | null>(null);
  const snapshotQuery = useIpcQuery('app-mode:get-snapshot', undefined, {
    enabled: capabilities.hasLocalFilesystem,
    staleTime: 10_000,
  });

  if (!capabilities.hasLocalFilesystem) return null;

  const snapshot = snapshotQuery.data;
  if (snapshot?.mode === 'cloud' || snapshot?.cloud.enabled === false) return null;

  const disabled = pending || snapshotQuery.isLoading;
  const title =
    error ?? (snapshotQuery.isLoading ? 'Loading Cloud Foundry availability' : undefined);

  // Apply a `set-mode` result: success → maybe refetch; blocked-by-preview →
  // surface the list for stop-and-switch; any other block → plain copy.
  const applySetModeResult = async (result: SetAppModeResult): Promise<void> => {
    if (!result.ok) {
      if (result.reason === 'active-local-preview' && (result.blockingPreviews?.length ?? 0) > 0) {
        setBlocking(result.blockingPreviews ?? []);
        setError(null);
        return;
      }
      setError(appModeBlockedLabel(result));
      return;
    }
    if (result.alreadyActive) {
      await snapshotQuery.refetch();
    }
  };

  const switchToCloud = async (): Promise<void> => {
    setError(null);
    setBlocking(null);
    setPending(true);
    try {
      await applySetModeResult(
        await window.crucible.invoke('app-mode:set-mode', { mode: 'cloud' }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  // Stop every RUNNING preview holding the switch, then retry. A launch still in
  // flight (no sessionId) can't be dev:stopped here; if one remains the retry
  // re-surfaces it rather than silently failing.
  const stopPreviewsAndSwitch = async (): Promise<void> => {
    if (!blocking) return;
    setError(null);
    setPending(true);
    try {
      for (const preview of blocking) {
        if (!preview.sessionId) continue;
        await window.crucible.invoke('dev:stop', {
          sessionId: preview.sessionId,
          gameId: preview.gameId,
        });
      }
      setBlocking(null);
      await applySetModeResult(
        await window.crucible.invoke('app-mode:set-mode', { mode: 'cloud' }),
      );
    } catch (err) {
      // #2259 (review) — surface the failure. The error panel only renders when
      // `!blocking`, so clear the blocking panel here; otherwise a `dev:stop`
      // (or retry) throw left the amber panel up and the red error invisible.
      setBlocking(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  const stoppableCount = blocking?.filter((p) => p.sessionId).length ?? 0;

  return (
    <div className={`app-region-no-drag relative shrink-0 ${className}`}>
      <AppModeSwitchButtonChrome
        label={pending ? 'Opening Cloud...' : 'Switch to Cloud Foundry'}
        icon={pending ? <RefreshCw size={14} className="animate-spin" /> : <Cloud size={14} />}
        onClick={() => void switchToCloud()}
        disabled={disabled}
        title={title}
      />
      {blocking && (
        <div
          role="status"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-72 rounded-md border border-amber-400/25 bg-amber-950/90 px-3 py-2 text-[12px] leading-5 text-amber-100 shadow-xl"
        >
          <div className="mb-1.5 font-medium">
            {/* Neutral wording — a blocker may be a launch still in flight, not
                a running preview (see the `launching` flag per row below). */}
            {blocking.length === 1
              ? 'A local preview is blocking the switch:'
              : `${blocking.length} local previews are blocking the switch:`}
          </div>
          <ul className="mb-2 space-y-0.5">
            {blocking.map((preview) => (
              <li key={preview.gameId} className="truncate font-mono text-[11px] text-amber-200">
                {preview.gameId}
                {preview.launching ? ' (launching…)' : ''}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={pending || stoppableCount === 0}
              onClick={() => void stopPreviewsAndSwitch()}
            >
              {pending ? 'Stopping...' : `Stop preview${stoppableCount === 1 ? '' : 's'} & switch`}
            </Button>
            <button
              type="button"
              className="text-amber-300/80 underline-offset-2 hover:underline"
              onClick={() => setBlocking(null)}
              disabled={pending}
            >
              Cancel
            </button>
          </div>
          {stoppableCount === 0 && (
            <div className="mt-1.5 text-amber-300/80">
              The preview is still launching — wait a moment, then try again.
            </div>
          )}
        </div>
      )}
      {error && !blocking && (
        <div
          role="status"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-64 rounded-md border border-red-400/25 bg-red-950/90 px-3 py-2 text-[12px] leading-5 text-red-100 shadow-xl"
        >
          {error}
        </div>
      )}
    </div>
  );
}
